import type { PaymentOptionView } from "@/lib/api/types";
import type { WalletProviderValue } from "@/modules/onboarding/constants/wallet-providers";
import {
  entriesToGuided,
  type InstaPayReceiver,
} from "@/modules/payment-options/lib/payment-option-mapping";

// Editable form shape for the payment section. The backend stores each method
// as a generic PaymentOption; this is the typed, per-method editor state.
// Bank is intentionally NOT represented here — existing BANK options are
// preserved untouched on save (see reconcile in payment-panel), they're just
// no longer surfaced in the UI.

// InstaPay receivers used in Egypt: a mobile number, an InstaPay address/link
// (e.g. name@instapay), a bank account (bank + account number + receiver name),
// or a card.
export type InstaReceiver = InstaPayReceiver;

export type WalletRow = {
  provider: WalletProviderValue;
  phone: string;
  // True while the phone was auto-filled from the account and not yet edited
  // — drives the "confirm it's your wallet number" hint.
  prefilled: boolean;
};

export type InstaState = {
  receiver: InstaReceiver;
  phone: string;
  ipa: string;
  cardNumber: string;
  cardHolder: string;
  bankName: string;
  holder: string;
  account: string;
};

export type PanelForm = {
  cod: boolean;
  autoConfirmCod: boolean;
  walletOn: boolean;
  wallets: WalletRow[];
  instaOn: boolean;
  insta: InstaState;
};

export const PHONE_RE = /^01\d{9}$/;

export const EMPTY_INSTA: InstaState = {
  receiver: "PHONE",
  phone: "",
  ipa: "",
  cardNumber: "",
  cardHolder: "",
  bankName: "",
  holder: "",
  account: "",
};

export function hydrate(
  options: PaymentOptionView[],
  acceptsCod: boolean,
  autoConfirmCodOrders: boolean,
): PanelForm {
  const wallets: WalletRow[] = [];
  let insta = EMPTY_INSTA;
  let instaOn = false;

  for (const opt of options) {
    const guided = entriesToGuided(opt.type, opt.entries);
    if (guided.kind === "wallet" && guided.provider) {
      wallets.push({ provider: guided.provider, phone: guided.phone, prefilled: false });
    } else if (guided.kind === "instapay" && !instaOn) {
      instaOn = true;
      insta = {
        receiver: guided.receiver,
        phone: guided.phone,
        ipa: guided.ipa,
        cardNumber: guided.cardNumber,
        cardHolder: guided.cardHolder,
        bankName: guided.bankName,
        holder: guided.holder,
        account: guided.account,
      };
    }
    // BANK (standalone) options are deliberately ignored (preserved, not surfaced).
  }

  return {
    cod: acceptsCod,
    autoConfirmCod: autoConfirmCodOrders,
    walletOn: wallets.length > 0,
    wallets,
    instaOn,
    insta,
  };
}

export function isInstaConfigured(insta: InstaState): boolean {
  if (insta.receiver === "PHONE") return insta.phone.trim().length > 0;
  if (insta.receiver === "IPA") return insta.ipa.trim().length > 0;
  if (insta.receiver === "CARD")
    return insta.cardNumber.trim().length > 0 && insta.cardHolder.trim().length > 0;
  // BANK_ACCOUNT
  return (
    insta.bankName.trim().length > 0 &&
    insta.holder.trim().length > 0 &&
    insta.account.trim().length > 0
  );
}

export function isWalletConfigured(wallets: WalletRow[]): boolean {
  return wallets.length > 0 && wallets.every((w) => w.phone.trim().length > 0);
}

// At least one online method enabled AND configured — gates the deposit card.
export function hasConfiguredOnline(f: PanelForm): boolean {
  const walletOk = f.walletOn && isWalletConfigured(f.wallets);
  const instaOk = f.instaOn && isInstaConfigured(f.insta);
  return walletOk || instaOk;
}

// True if any online method is switched on (regardless of whether its details
// are complete) — used to detect "turning off the last online method".
export function hasOnlineEnabled(f: PanelForm): boolean {
  return (f.walletOn && f.wallets.length > 0) || f.instaOn;
}
